import { memo, useCallback, useContext, useMemo, useState } from "react";
import { GoogleMap, GoogleMapProps, LoadScript } from "@react-google-maps/api";
import classNames from "classnames";
import { AppConfigContext } from "../../generic";
import { Loader, SimpleMapContext, ZoomContext } from "../../ui";
import { lightThemeMapCTG } from "./mapStyles";

const defaultCenterValue: google.maps.LatLngLiteral = {
  lat: 40.72,
  lng: -74,
};

const defaultMapOptions: google.maps.MapOptions = {
  mapTypeControl: false,
  zoomControl: true,
  streetViewControl: false,
  fullscreenControl: false,
  draggable: true,
  disableDoubleClickZoom: true,
  minZoom: 6,
  maxZoom: 20,
  styles: lightThemeMapCTG,
};

export type SimpleMapProps = GoogleMapProps & {
  className?: string,
}

export const SimpleMap = memo<SimpleMapProps>((
  {
    center = defaultCenterValue,
    zoom = 13,
    children,
    options,
    className,
    mapContainerClassName,
    onLoad,
    onUnmount,
    ...rest
  }) => {
  const { googleApiKey } = useContext(AppConfigContext);
  const [ map, setMap ] = useState<google.maps.Map | null>(null);
  const [ zoomForContext, setZoomForContext ] = useState<number>(zoom);

  const wrapperOnLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
    onLoad?.(map);
  }, [ onLoad ]);

  const wrapperOnUnmount = useCallback((map: google.maps.Map) => {
    setMap(null);
    onUnmount?.(map);
  }, [ setMap, onUnmount ]);

  const mapOptions = useMemo(() => ({
    ...defaultMapOptions,
    ...options,
  }), [ options ]);

  const onZoomChange = useCallback(() => {
    setZoomForContext(map?.getZoom()!);
  }, [ map ]);

  const zoomContext = useMemo(() => {return { zoom: zoomForContext };}, [ zoomForContext ]);
  return (
    <LoadScript
      googleMapsApiKey={googleApiKey}
      region={"US"}
      language={"en"}
      loadingElement={<Loader />}
    >
      <GoogleMap
        center={center}
        onLoad={wrapperOnLoad}
        onUnmount={wrapperOnUnmount}
        zoom={zoom}
        onZoomChanged={onZoomChange}
        mapContainerClassName={classNames(className, mapContainerClassName)}
        options={mapOptions}
        {...rest}
      >
        <SimpleMapContext.Provider value={map}>
          <ZoomContext.Provider value={zoomContext}>
            {children}
          </ZoomContext.Provider>

        </SimpleMapContext.Provider>
      </GoogleMap>
    </LoadScript>

  );
});

export default SimpleMap;
